import type { IVocabTermStats } from "../../../../lib/api"
import type { IDictionaryRow, TSortDir, TSortKey } from "./interfaces"

/** A term that was never heard after this many counted meetings is marked quiet. */
export const QUIET_AFTER_MEETINGS = 20

/** Never-heard (or never-in-window) terms after N counted meetings are prune candidates. */
export function isQuietTerm(term: { hits: number; lastSeenAt: string | null }, meetingsCounted: number): boolean {
  if (meetingsCounted < QUIET_AFTER_MEETINGS) return false
  return term.lastSeenAt === null || term.hits === 0
}

/** Overlay stats onto the listed terms. Missing stats → empty cells, not quiet. */
export function buildRows(
  terms: readonly string[],
  stats: readonly IVocabTermStats[] | undefined,
  meetingsCounted: number,
): IDictionaryRow[] {
  if (stats === undefined) {
    return terms.map((term) => ({
      term,
      hits: null,
      meetings: null,
      corrections: null,
      lastSeenAt: null,
      rank: null,
      quiet: false,
    }))
  }
  const byTerm = new Map(stats.map((s) => [s.term, s]))
  return terms.map((term) => {
    const s = byTerm.get(term)
    if (!s) {
      return {
        term,
        hits: null,
        meetings: null,
        corrections: null,
        lastSeenAt: null,
        rank: null,
        quiet: false,
      }
    }
    return {
      term,
      hits: s.hits,
      meetings: s.meetings,
      corrections: s.corrections,
      lastSeenAt: s.lastSeenAt,
      rank: s.rank,
      quiet: isQuietTerm(s, meetingsCounted),
    }
  })
}

/** Epoch ms for a nullable timestamp; invalid / unparseable values sort with nulls. */
function timeValue(value: string | null): number | null {
  if (!value) return null
  const direct = Date.parse(value)
  if (Number.isFinite(direct)) return direct
  const isoish = Date.parse(value.replace(" ", "T"))
  return Number.isFinite(isoish) ? isoish : null
}

/** Nulls always sort last, regardless of direction. */
function compareNullable(a: number | null, b: number | null, factor: number): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return (a - b) * factor
}

/** Default direction when switching to a column: rank/term A→Z, counts and dates most-interesting first. */
export function defaultSortDir(key: TSortKey): TSortDir {
  return key === "term" || key === "rank" ? "asc" : "desc"
}

/** Comparator for the chosen column + direction. Ties break on rank, then term. */
export function compareRows(a: IDictionaryRow, b: IDictionaryRow, key: TSortKey, dir: TSortDir): number {
  const factor = dir === "asc" ? 1 : -1
  let diff: number
  switch (key) {
    case "term":
      diff = a.term.localeCompare(b.term) * factor
      break
    case "hits":
      diff = compareNullable(a.hits, b.hits, factor)
      break
    case "meetings":
      diff = compareNullable(a.meetings, b.meetings, factor)
      break
    case "corrections":
      diff = compareNullable(a.corrections, b.corrections, factor)
      break
    case "rank":
      diff = compareNullable(a.rank, b.rank, factor)
      break
    case "lastSeenAt":
      diff = compareNullable(timeValue(a.lastSeenAt), timeValue(b.lastSeenAt), factor)
      break
  }
  if (diff !== 0) return diff
  const rankDiff = compareNullable(a.rank, b.rank, 1)
  if (rankDiff !== 0) return rankDiff
  return a.term.localeCompare(b.term)
}
